import { loadScript } from '../utils/dom';

// Local Storage keys
export const TIME_30 = 'nvLastSiteVisit';
export const TIME_1 = 'nvLastSiteVisit1';

export const URL_30 = 'nvEntryURL';
export const REFERRER_30 = 'nvReferrer';

export const URL_1 = 'nvEntryURL1';
export const REFERRER_1 = 'nvReferrer1';

export const LAST_CAMPAIGN = 'nvLastCampaign';
export const OD_REFERRAL_POST_ID = 'nvOdReferralPostId';

// Setup and run form attributions and update fields
export const useAttribution = (form) => {
  // Abort if browser does not support `localStorage`
  if (!window.localStorage) return;

  // Do not run attribution when disabled
  if (form && form.getAttribute('data-attribution') === 'disabled') {
    return {
      attributionFields: {
        lead_channel: 'disabled',
      },
    };
  }

  // First Touch: 60 day rolling attribution
  useRollingAttributionInterval({
    key: TIME_30,
    interval: 1000 * 60 * 60 * 24 * 60, // 60 days
    // interval: 1000 * 60 * 2, // 2 min for testing
    onExpired: () => {
      localStorage.setItem(URL_30, document.location.href);
      localStorage.setItem(REFERRER_30, document.referrer);
    },
  });

  // Last Touch: 1 day rolling attribution
  useRollingAttributionInterval({
    key: TIME_1,
    interval: 1000 * 60 * 60 * 24 * 1, // 1 day
    // interval: 1000 * 20, // 20 s for testing
    onExpired: () => {
      localStorage.setItem(URL_1, document.location.href);
      localStorage.setItem(REFERRER_1, document.referrer);
    },
  });

  // Find and set campaign value
  const campaign = useCampaignAttribution({
    key: LAST_CAMPAIGN,
  });

  let odReferralPostId;
  let customChannel;
  if (form) {
    // OD Referral form attribution
    odReferralPostId = useOdReferralAttribution({
      key: OD_REFERRAL_POST_ID,
      form,
    });

    // Custom channel attribution
    customChannel = form.getAttribute('data-channel') || '';
  }

  // Rolling 30 day attribution values
  const url30 = localStorage.getItem(URL_30);
  const referrer30 = localStorage.getItem(REFERRER_30);
  const attributionFields = getAttributionFields({
    isOd: !!odReferralPostId,
    channel: customChannel,
    url: url30,
    referrer: referrer30,
  });

  // Rolling 1 day attribution values
  const url1 = localStorage.getItem(URL_1);
  const referrer1 = localStorage.getItem(REFERRER_1);
  const attribution1 = getAttributionFields({
    isOd: !!odReferralPostId,
    channel: customChannel,
    url: url1,
    referrer: referrer1,
  });

  // Combine 60 and 1 day attribution if they are different
  if (
    !(
      attributionFields.lead_type === attribution1.lead_type &&
      attributionFields.lead_attribution === attribution1.lead_attribution
    )
  ) {
    attributionFields['lead_type_2'] = attribution1.lead_type;
    attributionFields['lead_attribution_2'] = attribution1.lead_attribution;
  }

  if (campaign) {
    attributionFields['campaign'] = campaign;
  }

  if (odReferralPostId) {
    attributionFields['od_referral_post_id'] = odReferralPostId;
  }

  // Set hidden field form values
  if (form) {
    setFormValues(form, attributionFields);
  }

  return {
    attributionFields,
  };
};

const getAttributionFields = ({ isOd = false, channel = false, url, referrer }) => {
  url = new URL(url);
  const params = new URLSearchParams(url.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');

  let referrerUrl;
  try {
    referrerUrl = new URL(referrer);
  } catch (error) {
    referrerUrl = {
      href: '',
      host: '',
    };
  }

  // Criteo
  // - original referrer contains `criteo.com` in URL, OR
  // - original `utm_source` is `criteo` (`utm_source=criteo`)
  const isCriteo = utmSource === 'criteo' || /criteo.com/.test(referrerUrl.host);

  // Google Ad detection
  const isAdWords =
    !!params.get('wbraid') ||
    !!params.get('gclid') ||
    (utmSource === 'google' && utmMedium === 'paid');

  // VSP special offer detection
  // Came in through /vsp-members/ or referrer from vsp.com
  const isVspOffer = url.href.includes('vsp-members') || /vsp.com/.test(referrerUrl.host);

  // Organic referrer detection
  // We have a referrer and it is not from this host
  const isOrganicReferrer =
    !!referrerUrl.href && !referrerUrl.href.includes(document.location.host);

  // Lead fields
  let leadType = 'Direct to Consumer (DTC)';
  let leadAttribution;
  let leadChannel;

  switch (true) {
    // Od Referral
    case isOd:
      leadType = 'Eye Care Professional (ECP) Referral';
      leadAttribution = 'OD Website';
      leadChannel = 'Book an Appointment';
      break;

    // Criteo
    case isCriteo:
      leadAttribution = 'Display';
      leadChannel = 'Criteo';
      break;

    // Google AdWords
    case isAdWords:
      leadAttribution = 'Paid';
      leadChannel = 'Google AdWords';
      break;

    // VSP Offer
    case isVspOffer:
      leadAttribution = 'VSP Offer';
      leadChannel = referrerUrl.host || 'vsp.com';
      break;

    // UTM Source or Medium
    case !!utmSource || !!utmMedium:
      leadAttribution = utmSource;
      leadChannel = utmMedium;
      break;

    // Custom Channel
    case !!channel:
      leadAttribution = isOrganicReferrer ? 'Organic' : 'Direct';
      leadChannel = channel;
      break;

    // Organic Referrer
    case isOrganicReferrer:
      leadAttribution = 'Organic';
      leadChannel = referrerUrl.host;
      break;

    // Default to Direct
    default:
      leadAttribution = 'Direct';
      leadChannel = 'Direct';
      break;
  }

  // Map attributions to form field names
  const data = {
    lead_type: leadType,
    lead_attribution: leadAttribution,
    lead_channel: leadChannel,
    original_entry_url: url.href,
    original_referrer_url: referrerUrl.href,
    form_submission_url: document.location.href,
  };

  return data;
};

// Create a rolling interval runner
export const useRollingAttributionInterval = ({ key, interval, onExpired }) => {
  const lastTime = localStorage.getItem(key);
  let isExpired = true;

  // Last time has been set, check if we are within the window
  if (lastTime) {
    isExpired = Number(lastTime) + interval < Date.now();
  }

  // If they have not been to the site within their rolling timer (or at all),
  // run expired function
  if (isExpired) {
    onExpired();
  }

  // Always reset the rolling timer
  localStorage.setItem(key, Date.now());
};

// Setup campaign attribution tracking using `utm_campaign` value
// Always replace the campaign value with a new one if found
const useCampaignAttribution = ({ key }) => {
  const currentParams = new URLSearchParams(document.location.search);
  let campaign = currentParams.get('utm_campaign');

  if (!campaign) {
    const campaignEl = document.querySelector('[data-custom-campaign]');
    if (campaignEl) {
      campaign = campaignEl.getAttribute('data-custom-campaign');
    }
  }

  if (campaign) {
    localStorage.setItem(key, campaign);
    return campaign;
  }

  return localStorage.getItem(key) || '';
};

// OD Referral form attribution, track post ID
const useOdReferralAttribution = ({ key, form }) => {
  const odReferralPostId = form.getAttribute('data-od-post-id');

  if (odReferralPostId) {
    // Set local storage value with post id
    localStorage.setItem(key, odReferralPostId);
    return odReferralPostId;
  }

  return localStorage.getItem(key) || '';
};

// Set attribution values onto form fields
const setFormValues = (form, values) => {
  Object.keys(values).forEach((key) => {
    let input = form.elements[key];
    const value = values[key];

    // If an input does not exist, create a hidden one
    if (!input) {
      input = document.createElement('input');
      input.setAttribute('name', key);
      input.setAttribute('type', 'hidden');
      input.setAttribute('value', value);
      form.appendChild(input);
    }

    input.value = value;
  });
};

// Criteo success tag to be fired on criteo attributed forms success handler
export const useCriteoSuccessTag = () => {
  loadScript('//static.criteo.net/js/ld/ld.js');

  window.criteo_q = window.criteo_q || [];

  return {
    fire: () => {
      var deviceType = /iPad/.test(navigator.userAgent)
        ? 't'
        : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent)
        ? 'm'
        : 'd';
      window.criteo_q.push(
        { event: 'setAccount', account: 74189 },
        { event: 'setSiteType', type: deviceType },
        { event: 'setEmail', email: '' },
        {
          event: 'trackTransaction',
          id: '' + new Date().getTime(),
          item: [{ id: '1', price: '0', quantity: 1 }],
        }
      );
    },
  };
};
